import { graphql } from "@/lib/generated"

export const SourcesQuery = graphql(`
  query Sources($status: SourceStatus, $providerType: String, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    sources(status: $status, providerType: $providerType, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        name
        slug
        providerType
        verifyConfig
        status
        rateLimitRps
        spikeProtection
        maxIngestRps
        brokerConfig
        responseConfig {
          statusCode
          body
          contentType
        }
        dedupConfig {
          strategy
          fields
          window
        }
        createdAt
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const SourceQuery = graphql(`
  query Source($id: UUID!) {
    source(id: $id) {
      id
      name
      slug
      providerType
      verifyConfig
      status
      rateLimitRps
      spikeProtection
      maxIngestRps
      brokerConfig
      responseConfig {
        statusCode
        body
        contentType
      }
      dedupConfig {
        strategy
        fields
        window
      }
      createdAt
      subscriptions {
        id
        name
        destinationId
        filterConfig {
          eventTypes
          regex
          bodyMatch {
            path
            value
            operator
          }
          rules {
            path
            operator
            value
            rules {
              path
              operator
              value
              rules {
                path
                operator
                value
              }
            }
          }
        }
        enabled
        destination {
          id
          name
          url
          status
        }
      }
    }
  }
`)

export const DestinationsQuery = graphql(`
  query Destinations($status: DestinationStatus, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    destinations(status: $status, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        name
        url
        type
        typeConfig
        deliveryMode
        status
        timeoutMs
        rateLimitRps
        retryPolicy {
          maxAttempts
          initialDelay
          maxDelay
          backoffFactor
        }
        headers
        authType
        ordered
        outputFormat
        healthScore
        disabledReason
        healthConfig {
          windowHours
          disableBelow
        }
        createdAt
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const DestinationQuery = graphql(`
  query Destination($id: UUID!) {
    destination(id: $id) {
      id
      name
      url
      type
      typeConfig
      signingSecret
      deliveryMode
      pollApiKeyPrefix
      pollApiKey
      status
      timeoutMs
      rateLimitRps
      retryPolicy {
        maxAttempts
        initialDelay
        maxDelay
        backoffFactor
      }
      headers
      authType
      oauth2Config {
        tokenUrl
        clientId
        clientSecret
        scopes
        audience
      }
      mtlsCert
      mtlsKey
      ordered
      outputFormat
      healthScore
      disabledReason
      healthConfig {
        windowHours
        disableBelow
      }
      connectorType
      autoDeletePayload
      metrics(hours: 24) {
        p50
        p95
        p99
        max
      }
      blockedDelivery {
        id
        eventId
        status
        attempts
        maxAttempts
        createdAt
        event {
          id
          eventType
        }
      }
      createdAt
      subscriptions {
        id
        name
        sourceId
        filterConfig {
          eventTypes
          regex
          bodyMatch {
            path
            value
            operator
          }
          rules {
            path
            operator
            value
            rules {
              path
              operator
              value
              rules {
                path
                operator
                value
              }
            }
          }
        }
        enabled
        source {
          id
          name
          slug
        }
      }
    }
  }
`)

export const SubscriptionsQuery = graphql(`
  query Subscriptions($sourceId: UUID, $destinationId: UUID, $enabled: Boolean, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    subscriptions(sourceId: $sourceId, destinationId: $destinationId, enabled: $enabled, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        name
        sourceId
        destinationId
        filterConfig {
          eventTypes
          regex
          bodyMatch {
            path
            value
            operator
          }
          rules {
            path
            operator
            value
            rules {
              path
              operator
              value
              rules {
                path
                operator
                value
              }
            }
          }
        }
        transformationId
        transformation {
          id
          name
        }
        enabled
        delay
        channels
        createdAt
        source {
          id
          name
          slug
        }
        destination {
          id
          name
          url
        }
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const SubscriptionQuery = graphql(`
  query Subscription($id: UUID!) {
    subscription(id: $id) {
      id
      name
      sourceId
      destinationId
      filterConfig {
        eventTypes
        regex
        bodyMatch {
          path
          value
          operator
        }
        rules {
          path
          operator
          value
          rules {
            path
            operator
            value
            rules {
              path
              operator
              value
            }
          }
        }
      }
      transformConfig {
        envelope
        headers
      }
      transformationId
      transformation {
        id
        name
      }
      enabled
      delay
      channels
      createdAt
      source {
        id
        name
        slug
      }
      destination {
        id
        name
        url
        status
      }
    }
  }
`)

export const EventsQuery = graphql(`
  query Events($sourceId: UUID, $eventType: String, $status: EventStatus, $payloadSearch: String, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    events(sourceId: $sourceId, eventType: $eventType, status: $status, payloadSearch: $payloadSearch, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        sourceId
        idempotencyKey
        eventType
        status
        receivedAt
        source {
          id
          name
          slug
        }
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const EventQuery = graphql(`
  query Event($id: UUID!) {
    event(id: $id) {
      id
      sourceId
      idempotencyKey
      eventType
      headers
      rawBody
      channels
      status
      receivedAt
      source {
        id
        name
        slug
      }
      deliveries {
        id
        destinationId
        status
        attempts
        maxAttempts
        nextAttemptAt
        createdAt
        destination {
          id
          name
          url
        }
      }
    }
  }
`)

export const DeliveriesQuery = graphql(`
  query Deliveries($eventId: UUID, $destinationId: UUID, $subscriptionId: UUID, $status: DeliveryStatus, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    deliveries(eventId: $eventId, destinationId: $destinationId, subscriptionId: $subscriptionId, status: $status, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        eventId
        subscriptionId
        destinationId
        status
        attempts
        maxAttempts
        nextAttemptAt
        createdAt
        destination {
          id
          name
          url
        }
        event {
          id
          eventType
          sourceId
        }
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const DeliveryQuery = graphql(`
  query Delivery($id: UUID!) {
    delivery(id: $id) {
      id
      eventId
      subscriptionId
      destinationId
      status
      attempts
      maxAttempts
      nextAttemptAt
      createdAt
      event {
        id
        eventType
        sourceId
        source {
          id
          name
          slug
        }
      }
      destination {
        id
        name
        url
      }
      subscription {
        id
        name
      }
      deliveryAttempts {
        id
        attemptNumber
        responseStatus
        responseBody
        error
        durationMs
        attemptedAt
      }
    }
  }
`)

export const DLQEntriesQuery = graphql(`
  query DLQEntries($eventId: UUID, $replayed: Boolean, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    dlqEntries(eventId: $eventId, replayed: $replayed, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        deliveryId
        eventId
        lastError
        replayedAt
        createdAt
        event {
          id
          eventType
          sourceId
        }
        delivery {
          id
          destinationId
          destination {
            id
            name
            url
          }
        }
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const ApiKeysQuery = graphql(`
  query ApiKeys($search: String, $limit: Int, $offset: Int) {
    apiKeys(search: $search, limit: $limit, offset: $offset) {
      nodes {
        id
        name
        keyPrefix
        scopes
        sourceIds
        createdAt
        expiresAt
        revokedAt
        lastUsedAt
      }
      pageInfo {
        total
        limit
        offset
      }
    }
  }
`)

export const ReplayDryRunQuery = graphql(`
  query ReplayDryRun($eventId: UUID!) {
    replayDryRun(eventId: $eventId) {
      event {
        id
        eventType
      }
      matches {
        subscription {
          id
          name
        }
        destination {
          id
          name
          url
        }
        wouldMatch
        matchReason
      }
    }
  }
`)

export const AlertRulesQuery = graphql(`
  query AlertRules($enabled: Boolean, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    alertRules(enabled: $enabled, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        name
        conditionType
        threshold
        webhookUrl
        channel
        emailConfig {
          to
          subjectTemplate
        }
        slackConfig {
          webhookUrl
          channel
        }
        cooldown
        enabled
        createdAt
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const AlertRuleQuery = graphql(`
  query AlertRule($id: UUID!) {
    alertRule(id: $id) {
      id
      name
      conditionType
      threshold
      webhookUrl
      channel
      emailConfig {
        to
        subjectTemplate
      }
      slackConfig {
        webhookUrl
        channel
      }
      cooldown
      enabled
      createdAt
    }
  }
`)

export const DashboardTrendsQuery = graphql(`
  query DashboardTrends($hours: Int) {
    dashboardTrends(hours: $hours) {
      eventsReceived {
        timestamp
        value
      }
      deliveriesCompleted {
        timestamp
        value
      }
      deliveriesFailed {
        timestamp
        value
      }
      outboundMessages {
        timestamp
        value
      }
    }
  }
`)

export const BookmarksQuery = graphql(`
  query Bookmarks($eventId: UUID, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    bookmarks(eventId: $eventId, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        eventId
        name
        notes
        createdAt
        event {
          id
          eventType
          sourceId
          source {
            id
            name
            slug
          }
        }
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const BookmarkByEventQuery = graphql(`
  query BookmarkByEvent($eventId: UUID!) {
    bookmarks(eventId: $eventId, limit: 1) {
      nodes {
        id
        name
        notes
      }
    }
  }
`)

export const EventTypeSchemasQuery = graphql(`
  query EventTypeSchemas($search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    eventTypeSchemas(search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        eventType
        description
        schema
        example
        createdAt
        updatedAt
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const EventTypeSchemaQuery = graphql(`
  query EventTypeSchema($id: UUID!) {
    eventTypeSchema(id: $id) {
      id
      eventType
      description
      schema
      example
      createdAt
      updatedAt
    }
  }
`)

export const TransformationsQuery = graphql(`
  query Transformations($enabled: Boolean, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    transformations(enabled: $enabled, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        name
        description
        code
        enabled
        failOpen
        timeoutMs
        envVars
        createdAt
        updatedAt
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const TransformationQuery = graphql(`
  query Transformation($id: UUID!) {
    transformation(id: $id) {
      id
      name
      description
      code
      enabled
      failOpen
      timeoutMs
      envVars
      createdAt
      updatedAt
    }
  }
`)

export const PortalLinksQuery = graphql(`
  query PortalLinks($search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    portalLinks(search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        token
        scope {
          sourceId
          eventId
          destinationId
          applicationId
        }
        expiresAt
        createdAt
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const StatusQuery = graphql(`
  query Status {
    status {
      status
      dlqSize
      outboundDlqSize
      queueDepth
      activeWorkers
      totalWorkers
      uptime
      version
      sourcesTotal
      destinationsTotal
      subscriptionsTotal
      eventsTotal
      eventsFailed
      deliveriesTotal
      deliveriesPending
      deliveriesDelivered
      messagesTotal
      outboundDeliveriesTotal
      outboundDeliveriesPending
      outboundDeliveriesFailed
    }
  }
`)

export const PollDeliveriesQuery = graphql(`
  query PollDeliveries($destinationId: UUID!, $cursor: String, $limit: Int) {
    pollDeliveries(destinationId: $destinationId, cursor: $cursor, limit: $limit) {
      nodes {
        id
        eventId
        subscriptionId
        destinationId
        status
        attempts
        maxAttempts
        createdAt
        event {
          id
          eventType
          sourceId
        }
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const AuditLogsQuery = graphql(`
  query AuditLogs($actorType: String, $resourceType: String, $action: String, $search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    auditLogs(actorType: $actorType, resourceType: $resourceType, action: $action, search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        actorType
        actorId
        actorName
        action
        resourceType
        resourceId
        ipAddress
        userAgent
        details
        createdAt
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const AuditLogQuery = graphql(`
  query AuditLog($id: UUID!) {
    auditLog(id: $id) {
      id
      actorType
      actorId
      actorName
      action
      resourceType
      resourceId
      ipAddress
      userAgent
      details
      createdAt
    }
  }
`)

export const OrganizationsQuery = graphql(`
  query Organizations($search: String, $limit: Int, $offset: Int) {
    organizations(search: $search, limit: $limit, offset: $offset) {
      nodes {
        id
        name
        slug
        ssoEnabled
        ssoProvider
        retentionEvents
        retentionMessages
        otlpConfig {
          endpoint
          headers
          insecure
          sampleRate
        }
        metricsExportConfig {
          type
          apiKey
          endpoint
          interval
        }
        createdAt
        updatedAt
      }
      pageInfo {
        total
        limit
        offset
        hasNextPage
      }
    }
  }
`)

export const UsersQuery = graphql(`
  query Users($organizationId: UUID, $search: String, $limit: Int, $offset: Int) {
    users(organizationId: $organizationId, search: $search, limit: $limit, offset: $offset) {
      nodes {
        id
        organizationId
        email
        name
        role
        lastLoginAt
        createdAt
      }
      pageInfo {
        total
        limit
        offset
        hasNextPage
      }
    }
  }
`)

export const MeQuery = graphql(`
  query Me {
    me {
      id
      email
      name
      role
      organizationId
    }
  }
`)

export const ProvidersQuery = graphql(`
  query Providers {
    providers
  }
`)

export const ActiveTunnelsQuery = graphql(`
  query ActiveTunnels {
    activeTunnels {
      id
      slug
      sourceUrl
    }
  }
`)

export const MetaEventConfigsQuery = graphql(`
  query MetaEventConfigs($search: String, $limit: Int, $offset: Int, $after: String, $first: Int) {
    metaEventConfigs(search: $search, limit: $limit, offset: $offset, after: $after, first: $first) {
      nodes {
        id
        name
        url
        signingSecret
        eventTypes
        enabled
        createdAt
      }
      pageInfo {
        total
        limit
        offset
        endCursor
        hasNextPage
      }
    }
  }
`)

export const MetaEventConfigQuery = graphql(`
  query MetaEventConfig($id: UUID!) {
    metaEventConfig(id: $id) {
      id
      name
      url
      signingSecret
      eventTypes
      enabled
      createdAt
    }
  }
`)
